/* ============================================================
   OSZEA — RainClub page interactions
   Vanilla JS, dependency-free, Shopify-friendly.
   - Challenge data is the single source of truth for the
     card grid, the map pins and the map rail.
   - localStorage powers task completion + saved/done state
     (no login required for basic use).
   ============================================================ */
(function () {
  'use strict';
  var $ = function (s, c) { return (c || document).querySelector(s); };
  var $$ = function (s, c) { return Array.prototype.slice.call((c || document).querySelectorAll(s)); };
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var LS = {
    get: function (k, d) { try { return JSON.parse(localStorage.getItem(k)) || d; } catch (e) { return d; } },
    set: function (k, v) { try { localStorage.setItem(k, JSON.stringify(v)); } catch (e) {} }
  };

  /* ============================================================
     PROGRESS BACKUP — save to email with a portable restore link.
     No login, no password, no backend: the full progress snapshot
     is encoded into the link itself (…#restore=CODE). Opening the
     link on any device restores walks, saves and tracker state.
     ============================================================ */
  var PROGRESS_KEYS = ['oszea_rc_30', 'oszea_rc_done', 'oszea_rc_saved'];
  function snapshotProgress(email) {
    return { v: 1, e: email || LS.get('oszea_rc_email', '') || '', t: Date.now(), p: {
      'oszea_rc_30': LS.get('oszea_rc_30', null),
      'oszea_rc_done': LS.get('oszea_rc_done', {}),
      'oszea_rc_saved': LS.get('oszea_rc_saved', {})
    } };
  }
  function encodeProgress(obj) { try { return btoa(unescape(encodeURIComponent(JSON.stringify(obj)))); } catch (e) { return ''; } }
  function decodeProgress(str) { try { return JSON.parse(decodeURIComponent(escape(atob(String(str).trim())))); } catch (e) { return null; } }
  function applyProgress(obj) {
    if (!obj || !obj.p) return false;
    PROGRESS_KEYS.forEach(function (k) { if (obj.p[k] != null) LS.set(k, obj.p[k]); });
    if (obj.e) LS.set('oszea_rc_email', obj.e);
    return true;
  }
  function restoreLink(email) {
    var code = encodeProgress(snapshotProgress(email));
    return location.origin + location.pathname + '#restore=' + encodeURIComponent(code);
  }
  // Auto-restore from a link BEFORE the feature modules read storage
  (function autoRestore() {
    var m = location.hash.match(/[#&]restore=([^&]+)/); if (!m) return;
    var obj = decodeProgress(decodeURIComponent(m[1]));
    if (obj && applyProgress(obj)) {
      try { history.replaceState(null, '', location.pathname + location.search + '#moments'); } catch (e) {}
      window.__rcRestored = obj.e || true;
    }
  })();

  /* ============================================================
     CHALLENGE DATA  (editable — maps to Shopify section blocks)
     ============================================================ */
  var CHALLENGES = [
    // ---- NEW ZEALAND ----
    { id: 'nz1', n: 1, title: 'Rangitoto Summit Walk', loc: 'Auckland', country: 'nz', flag: '🇳🇿',
      type: 'Volcano · harbour views', diff: 'Moderate', diffKey: 'hike', time: '2–3 hrs', cond: 'Sea breeze',
      tags: ['nz', 'hike', 'coastal', 'water'],
      task: 'Walk without rushing. Notice the contrast between black lava rock, sea air, and sky.',
      reflect: 'What feels old in your life that you are ready to move through?',
      notice: 'Black lava rock · sea air · open sky', fit: 'Tasman Essentials', x: 77, y: 29 , img: 'Rangitoto Summit Walk.png' },
    { id: 'nz2', n: 2, title: 'Waitākere Ranges Forest Reset', loc: 'Auckland', country: 'nz', flag: '🇳🇿',
      type: 'Forest · waterfall area', diff: 'Easy–Moderate', diffKey: 'easy', time: '1–2 hrs', cond: 'Often damp',
      tags: ['nz', 'easy', 'forest', 'water', 'rainy'],
      task: 'Pause under the trees for one full minute and listen before taking photos.',
      reflect: 'What sound made you feel present?',
      notice: 'Birdsong · falling water · soft light', fit: 'Tasman Essentials', x: 74, y: 31 , img: 'Waitakere Ranges Forest Reset.png' },
    { id: 'nz3', n: 3, title: 'Queenstown Hill Time Walk', loc: 'Queenstown', country: 'nz', flag: '🇳🇿',
      type: 'Hill · lake views', diff: 'Moderate', diffKey: 'hike', time: '2–3 hrs', cond: 'Crisp & changeable',
      tags: ['nz', 'hike', 'water'],
      task: 'At the lookout, take five slow breaths and look at the distance without checking your phone.',
      reflect: 'What problem feels smaller from up here?',
      notice: 'Lake colour · distant peaks · wind', fit: 'Tasman Essentials', x: 67, y: 61 , img: 'Queenstown Hill Time Walk.png' },
    { id: 'nz4', n: 4, title: 'Ben Lomond Track Moment', loc: 'Queenstown', country: 'nz', flag: '🇳🇿',
      type: 'Mountain', diff: 'Hard', diffKey: 'hike', time: '5–6 hrs', cond: 'Alpine — check first',
      tags: ['nz', 'hike'],
      task: 'Choose a safe section and focus only on the next ten steps.',
      reflect: 'What does patience feel like today?',
      notice: 'Rhythm of steps · thinning air · summit light', fit: 'Tasman Essentials', x: 65, y: 63 , img: 'Ben Lomond Track Moment.png' },
    { id: 'nz5', n: 5, title: 'Hooker Valley Track', loc: 'Aoraki / Mt Cook', country: 'nz', flag: '🇳🇿',
      type: 'Alpine valley · water', diff: 'Easy–Moderate', diffKey: 'easy', time: '3 hrs', cond: 'Cold & clear',
      tags: ['nz', 'easy', 'water'],
      task: 'Notice how water moves through the valley. Let that movement guide your pace.',
      reflect: 'What are you ready to stop holding so tightly?',
      notice: 'Glacial rivers · swing bridges · Aoraki', fit: 'Tasman Essentials', x: 68, y: 57 , img: 'Hooker Valley Track.png' },
    { id: 'nz6', n: 6, title: 'Tongariro Alpine Mindful Miles', loc: 'Central North Island', country: 'nz', flag: '🇳🇿',
      type: 'Alpine · volcanic', diff: 'Hard', diffKey: 'hike', time: '6–8 hrs', cond: 'Exposed — prepare',
      tags: ['nz', 'hike'],
      task: 'Respect the conditions. Walk prepared. Notice colour, rock, wind, and breath.',
      reflect: 'Where in life do you need better preparation?',
      notice: 'Crater colour · volcanic rock · wind', fit: 'Tasman Essentials', x: 76, y: 38 , img: 'Tongariro Alpine Mindful Miles.png' },
    { id: 'nz7', n: 7, title: 'Abel Tasman Coastal Walk', loc: 'Tasman', country: 'nz', flag: '🇳🇿',
      type: 'Coastal · beach · forest', diff: 'Easy–Moderate', diffKey: 'easy', time: '2–4 hrs', cond: 'Mild & bright',
      tags: ['nz', 'easy', 'coastal', 'forest', 'water'],
      task: 'Walk near the water and notice how your pace changes beside the sea.',
      reflect: 'What helps you feel lighter?',
      notice: 'Golden sand · clear water · birdsong', fit: 'Tasman Essentials', x: 72, y: 47 , img: 'Abel Tasman Coastal Walk.png' },
    { id: 'nz8', n: 8, title: 'Mauao Base Track', loc: 'Mount Maunganui', country: 'nz', flag: '🇳🇿',
      type: 'Coastal city walk', diff: 'Easy', diffKey: 'easy', time: '45 min', cond: 'Breezy',
      tags: ['nz', 'easy', 'coastal', 'city', 'water'],
      task: 'Walk one loop and notice the people, waves, wind, and your own breathing.',
      reflect: 'What small habit could you repeat weekly?',
      notice: 'Waves · joggers · open horizon', fit: 'Urban Essentials', x: 79, y: 33, img: 'Mauao Base Track.png' },
    { id: 'nz9', n: 9, title: 'Wellington Southern Walkway', loc: 'Wellington', country: 'nz', flag: '🇳🇿',
      type: 'City · hills · wind', diff: 'Moderate', diffKey: 'hike', time: '2–3 hrs', cond: 'Famously windy',
      tags: ['nz', 'hike', 'city', 'coastal'],
      task: 'Feel the wind without fighting it. Let it remind you that resistance uses energy.',
      reflect: 'Where can you soften today?',
      notice: 'Gusts · ridgelines · the harbour below', fit: 'Tasman Essentials + Elements Bucket Hat', x: 75, y: 45, img: 'Wellington Southern Walkway.png' },
    { id: 'nz10', n: 10, title: 'Port Hills Reset', loc: 'Christchurch', country: 'nz', flag: '🇳🇿',
      type: 'Hills · city views', diff: 'Easy–Moderate', diffKey: 'easy', time: '1–2 hrs', cond: 'Cool & dry',
      tags: ['nz', 'easy', 'city'],
      task: 'Walk until the city feels quiet behind you.',
      reflect: 'What did distance help you see clearly?',
      notice: 'City fading · tussock · the plains', fit: 'Urban or Tasman Essentials', x: 71, y: 54, img: 'Port Hills Reset.png' },
    // ---- AUSTRALIA ----
    { id: 'au1', n: 11, title: 'Bondi to Coogee Coastal Walk', loc: 'Sydney', country: 'au', flag: '🇦🇺',
      type: 'Coastal', diff: 'Easy–Moderate', diffKey: 'easy', time: '2 hrs', cond: 'Sea air',
      tags: ['au', 'easy', 'coastal', 'city', 'water'],
      task: 'Walk beside the ocean and match your breathing to the waves.',
      reflect: 'What rhythm do you want to return to?',
      notice: 'Swell · sandstone · salt air', fit: 'Tasman Essentials', x: 43, y: 53 , img: 'Bondi to Coogee Coastal Walk.png' },
    { id: 'au2', n: 12, title: 'Three Sisters Scenic Walk', loc: 'Blue Mountains, NSW', country: 'au', flag: '🇦🇺',
      type: 'Mountains · lookout', diff: 'Easy–Moderate', diffKey: 'easy', time: '2–3 hrs', cond: 'Cool highland',
      tags: ['au', 'easy', 'hike', 'forest'],
      task: 'Pause at a lookout and name three things you can see, hear, and feel.',
      reflect: 'What feels bigger than your worries today?',
      notice: 'Blue haze · eucalypt · deep valleys', fit: 'Tasman Essentials', x: 40, y: 51 , img: 'Three Sisters Scenic Walk.png' },
    { id: 'au3', n: 13, title: 'Royal NP Coast Track Moment', loc: 'Sydney region', country: 'au', flag: '🇦🇺',
      type: 'Coastal national park', diff: 'Moderate–Hard', diffKey: 'hike', time: '4–6 hrs', cond: 'Exposed coast',
      tags: ['au', 'hike', 'coastal', 'forest', 'water'],
      task: 'Choose a safe section and walk slowly enough to notice changing textures underfoot.',
      reflect: 'What changes when you slow down?',
      notice: 'Cliffs · heath · hidden beaches', fit: 'Tasman Essentials', x: 42, y: 55 , img: 'Royal NP Coast Track Moment.png' },
    { id: 'au4', n: 14, title: 'Dandenong Ranges 1000 Steps', loc: 'Melbourne', country: 'au', flag: '🇦🇺',
      type: 'Forest · steps', diff: 'Moderate', diffKey: 'hike', time: '1.5 hrs', cond: 'Cool & green',
      tags: ['au', 'hike', 'forest'],
      task: 'Count your breath, not your steps.',
      reflect: 'What helps you keep going?',
      notice: 'Tree ferns · birdcall · cool air', fit: 'Urban Essentials', x: 34, y: 66, img: 'Dandenong Ranges 1000 Steps.png' },
    { id: 'au5', n: 15, title: 'The Pinnacle Walk', loc: 'Grampians, VIC', country: 'au', flag: '🇦🇺',
      type: 'Rock · lookout', diff: 'Moderate', diffKey: 'hike', time: '2–3 hrs', cond: 'Warm & open',
      tags: ['au', 'hike'],
      task: 'At the top, take one photo and then put your phone away for two minutes.',
      reflect: 'What moment did you keep just for yourself?',
      notice: 'Rock faces · ridgelines · big sky', fit: 'Tasman Essentials', x: 30, y: 64 , img: 'The Pinnacle Walk.png' },
    { id: 'au6', n: 16, title: 'Noosa NP Coastal Walk', loc: 'Queensland', country: 'au', flag: '🇦🇺',
      type: 'Coastal · forest', diff: 'Easy–Moderate', diffKey: 'easy', time: '1–2 hrs', cond: 'Warm & humid',
      tags: ['au', 'easy', 'coastal', 'forest', 'water'],
      task: 'Notice heat, breeze, shade, and ocean — let your body guide the pace.',
      reflect: 'What does ease feel like?',
      notice: 'Pandanus · turquoise bays · warm breeze', fit: 'Elements Bucket Hat + Urban Essentials', x: 44, y: 34, img: 'Noosa NP Coastal Walk.png' },
    { id: 'au7', n: 17, title: 'Mount Coot-tha Nature Reset', loc: 'Brisbane', country: 'au', flag: '🇦🇺',
      type: 'City nature · lookout', diff: 'Easy–Moderate', diffKey: 'easy', time: '1–2 hrs', cond: 'Warm',
      tags: ['au', 'easy', 'city', 'forest'],
      task: 'Walk before or after work and let it become a transition ritual.',
      reflect: 'What do you want to leave behind before going home?',
      notice: 'City skyline · gum trees · dusk light', fit: 'Urban Essentials', x: 45, y: 40, img: 'Mount Coot-tha Nature Reset.png' },
    { id: 'au8', n: 18, title: 'Waterfall Gully to Mt Lofty', loc: 'Adelaide', country: 'au', flag: '🇦🇺',
      type: 'Hills · waterfall · lookout', diff: 'Moderate', diffKey: 'hike', time: '1.5–2 hrs', cond: 'Mild',
      tags: ['au', 'hike', 'forest', 'water'],
      task: 'Listen for water first, then walk toward the view.',
      reflect: 'What are you moving toward?',
      notice: 'Falling water · gum forest · the summit view', fit: 'Tasman Essentials', x: 28, y: 61 , img: 'Waterfall Gully to Mt Lofty.png' },
    { id: 'au9', n: 19, title: 'Dove Lake Circuit', loc: 'Cradle Mountain, TAS', country: 'au', flag: '🇦🇺',
      type: 'Alpine lake', diff: 'Easy–Moderate', diffKey: 'easy', time: '2–3 hrs', cond: 'Cold & moody',
      tags: ['au', 'easy', 'water', 'rainy'],
      task: 'Walk around water and notice how stillness and movement can exist together.',
      reflect: 'Where do you need both calm and action?',
      notice: 'Still water · jagged peaks · button grass', fit: 'Tasman Essentials', x: 37, y: 77 , img: 'Dove Lake Circuit.png' },
    { id: 'au10', n: 20, title: 'Cape to Cape Coastal Reset', loc: 'Western Australia', country: 'au', flag: '🇦🇺',
      type: 'Coast · cliffs · beach', diff: 'Moderate', diffKey: 'hike', time: '2–4 hrs', cond: 'Windy coast',
      tags: ['au', 'hike', 'coastal', 'water'],
      task: 'Walk a short safe section and focus on horizon breathing.',
      reflect: 'What feels possible when you look further ahead?',
      notice: 'Granite headlands · long beaches · the horizon', fit: 'Tasman Essentials + Elements Bucket Hat', x: 9, y: 58, img: 'Cape to Cape Coastal Reset.png' }
  ];

  /* Real coordinates + landscape category (added for the Leaflet map + landscape filters) */
  var GEO = {
    nz1:  [-36.7865, 174.8595, 'coastal'], nz2:  [-36.9450, 174.5050, 'forest'],
    nz3:  [-45.0200, 168.6850, 'mountain'], nz4:  [-44.9700, 168.6500, 'mountain'],
    nz5:  [-43.7330, 170.0960, 'mountain'], nz6:  [-39.1330, 175.6500, 'mountain'],
    nz7:  [-40.9000, 173.0500, 'coastal'], nz8:  [-37.6320, 176.1830, 'coastal'],
    nz9:  [-41.3110, 174.7800, 'city'],     nz10: [-43.5900, 172.6600, 'city'],
    au1:  [-33.8915, 151.2767, 'coastal'], au2:  [-33.7320, 150.3120, 'mountain'],
    au3:  [-34.1300, 151.0700, 'coastal'], au4:  [-37.8900, 145.3500, 'forest'],
    au5:  [-37.1500, 142.5200, 'mountain'], au6:  [-26.3900, 153.1000, 'coastal'],
    au7:  [-27.4780, 152.9500, 'city'],     au8:  [-34.9700, 138.7000, 'forest'],
    au9:  [-41.6600, 145.9500, 'mountain'], au10: [-33.9550, 115.0700, 'coastal']
  };
  CHALLENGES.forEach(function (c) {
    var g = GEO[c.id]; if (g) { c.lat = g[0]; c.lng = g[1]; c.land = g[2]; if (c.tags.indexOf(g[2]) < 0) c.tags.push(g[2]); }
  });

  var FIT_LINKS = {
    'Tasman': '/collections/all',
    'Urban': '/collections/all',
    'Kids': '/collections/all',
    'Elements': '/pages/zeatex'
  };
  function fitLink(fit) {
    for (var k in FIT_LINKS) { if (fit.indexOf(k) >= 0) return FIT_LINKS[k]; }
    return '/collections/all';
  }
  function fitImage(fit) {
    if (!window.__OSZEA_ASSET_BASE) return '';
    if (fit.indexOf('Tasman') >= 0) return window.__OSZEA_ASSET_BASE + 'tasman-onyx.png';
    return window.__OSZEA_ASSET_BASE + 'tech-hero.png';
  }

  /* ---------- small icon helpers ---------- */
  var I = {
    pin: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 21s7-6.3 7-12a7 7 0 1 0-14 0c0 5.7 7 12 7 12Z"/><circle cx="12" cy="9" r="2.5"/></svg>',
    clock: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>',
    foot: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-9 8.3L3 21l1.5-6A8.5 8.5 0 1 1 21 11.5z"/></svg>',
    check: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="m5 12 5 5L20 6"/></svg>',
    heart: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/></svg>',
    eye: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7Z"/><circle cx="12" cy="12" r="3"/></svg>',
    leaf: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.5 19 2c1 2 2 4.18 2 8a7 7 0 0 1-7 7Z"/><path d="M2 21c2-2 4-4 8-6"/></svg>',
    droplet: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3s7 7 7 12a7 7 0 0 1-14 0c0-5 7-12 7-12Z"/></svg>',
    wave: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 8c2.5 0 2.5 2 5 2s2.5-2 5-2 2.5 2 5 2 2.5-2 5-2"/><path d="M2 14c2.5 0 2.5 2 5 2s2.5-2 5-2 2.5 2 5 2 2.5-2 5-2"/></svg>',
    mountain: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m3 20 6-11 4 6 2-3 6 8z"/></svg>',
    tree: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3 6 12h3l-3 5h12l-3-5h3z"/><path d="M12 17v4"/></svg>',
    building: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 21V5l8-2v18M14 21V9l6 2v10"/><path d="M8 8h0M8 12h0M8 16h0"/></svg>',
    rain: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M7 15a5 5 0 0 1 .5-9.95A7 7 0 1 1 19 13"/><path d="M8 19l-1 2M12 19l-1 2M16 19l-1 2"/></svg>'
  };

  /* ============================================================
     SCROLL REVEAL
     ============================================================ */
  var io = new IntersectionObserver(function (es) {
    es.forEach(function (e) { if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); } });
  }, { threshold: 0.1, rootMargin: '0px 0px -6% 0px' });
  function observeReveals() { $$('.reveal:not(.in)').forEach(function (el) { io.observe(el); }); }
  observeReveals();

  /* smooth scroll */
  $$('[data-scroll]').forEach(function (a) {
    a.addEventListener('click', function (e) {
      var href = a.getAttribute('href') || '';
      if (href.charAt(0) !== '#') return;
      var t = $(href); if (!t) return;
      e.preventDefault();
      var y = t.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top: y, behavior: reduce ? 'auto' : 'smooth' });
    });
  });

  /* ============================================================
     TOAST
     ============================================================ */
  var toastZone = $('#toastZone');
  function toast(msg) {
    if (!toastZone) return;
    var t = document.createElement('div');
    t.className = 'toast';
    t.innerHTML = '<span class="tk">' + I.check + '</span><span>' + msg + '</span>';
    toastZone.appendChild(t);
    requestAnimationFrame(function () { t.classList.add('show'); });
    setTimeout(function () { t.classList.remove('show'); setTimeout(function () { t.remove(); }, 400); }, 3000);
  }

  /* ============================================================
     30-DAY CHALLENGE SELECTOR
     ============================================================ */
  (function challenge30() {
    var modes = {
      weekend: { days: 8,  label: 'Weekend Wander',          unit: 'walks' },
      reset7:  { days: 7,  label: '7-Day Reset',             unit: 'days' },
      habit14: { days: 14, label: '14-Day Nature Habit',     unit: 'days' },
      full30:  { days: 30, label: '30-Day RainClub Challenge', unit: 'days' }
    };
    var TASKS = [
      'Step outside for ten minutes — no headphones, no destination',
      'Before you reach for your phone, name five sounds you can hear',
      'Walk just after the rain and follow where the water goes',
      'Do the breathing reset on your doorstep before you leave',
      'Notice the first thing today that makes you slow down',
      'Find a tree and stand under it for one full minute',
      'Name one thing that feels lighter than it did yesterday',
      'Take a street you’ve walked past but never walked down',
      'Stand still for sixty seconds and do nothing but breathe',
      'Notice the temperature of the air on your face and hands',
      'Walk toward water — a river, the sea, even a puddle',
      'Keep your phone pocketed the whole way there and back',
      'Watch the sky change for two slow, unhurried minutes',
      'Catch one stranger’s eye and say good morning',
      'Walk slower than feels natural and notice what you’d have missed',
      'Find shelter from a shower and let the rain do the talking',
      'Spot three colours you usually walk straight past',
      'Reach a high point, stop, and take five long breaths',
      'Swap ten minutes of scrolling for ten minutes outside',
      'Let the wind hit you and consciously drop your shoulders',
      'Leave one stretch of path a little better than you found it',
      'Notice where your breath sits today — your chest or your belly?',
      'Find something green to walk through on your lunch break',
      'Trace a single raindrop’s path along the ground',
      'Breathe in for four, hold for two, out for six — five times',
      'Seek out the quietest spot within ten minutes of you',
      'Feel the ground underfoot — grass, sand, or stone',
      'Follow one bird with your eyes for as long as it stays',
      'Ask yourself what’s changed in you since day one',
      'Invite someone to come on your next walk'
    ];
    var weekendTasks = [TASKS[0], TASKS[2], TASKS[3], TASKS[5], TASKS[10], TASKS[15], TASKS[19], TASKS[6]];
    var REFLECTIONS = [
      'One week in — what feels a little lighter than the day you started?',
      'Two weeks of showing up — what’s the smallest change you’ve felt, and where?',
      'Three weeks — which walk keeps coming back to you when you close your eyes?',
      'A full month outside — what did the weather teach you that you want to keep?'
    ];

    var modeBtns = $$('.rc-mode');
    var titleEl = $('#rc30Title'), subEl = $('#rc30Sub'),
        grid = $('#rc30Grid'), ringFg = $('#rc30RingFg'), ringTxt = $('#rc30RingTxt'),
        doneEl = $('#rc30Done'), streakEl = $('#rc30Streak'), leftEl = $('#rc30Left'),
        focusTask = $('#rc30FocusTask'), focusBtn = $('#rc30FocusBtn'),
        reflectBox = $('#rc30Reflect'), reflectQ = $('#rc30ReflectQ'),
        confetti = $('#rc30Confetti');
    if (!grid) return;

    var RAD = 24, CIRC = 2 * Math.PI * RAD;
    if (ringFg) { ringFg.style.strokeDasharray = CIRC; ringFg.style.strokeDashoffset = CIRC; }

    var state = LS.get('oszea_rc_30', { mode: 'full30', done: {} });
    if (!modes[state.mode]) state.mode = 'full30';

    function tasksFor(mode) { return mode === 'weekend' ? weekendTasks : TASKS.slice(0, modes[mode].days); }
    function key(mode, i) { return mode + ':' + i; }
    function isDone(i) { return !!state.done[key(state.mode, i)]; }
    function doneCount() { var t = tasksFor(state.mode), n = 0; for (var i = 0; i < t.length; i++) if (isDone(i)) n++; return n; }
    function streak() { var t = tasksFor(state.mode), s = 0; for (var i = 0; i < t.length; i++) { if (isDone(i)) s++; else break; } return s; }
    function firstUndone() { var t = tasksFor(state.mode); for (var i = 0; i < t.length; i++) if (!isDone(i)) return i; return -1; }

    function buildGrid() {
      var tasks = tasksFor(state.mode);
      grid.style.setProperty('--cols', tasks.length > 14 ? 10 : (tasks.length > 7 ? 7 : tasks.length));
      grid.innerHTML = tasks.map(function (txt, i) {
        return '<button class="rc-drop' + (isDone(i) ? ' done' : '') + '" data-i="' + i + '" type="button" title="' +
          txt.replace(/"/g, '&quot;') + '" aria-label="Day ' + (i + 1) + ': ' + txt.replace(/"/g, '&quot;') + '">' +
          '<svg class="dp" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2.5s7 7.6 7 12.4a7 7 0 0 1-14 0C5 10.1 12 2.5 12 2.5Z"/></svg>' +
          '<span class="dn">' + (i + 1) + '</span>' +
          '<svg class="dk" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m5 12 5 5L20 6"/></svg>' +
          '</button>';
      }).join('');
      $$('.rc-drop', grid).forEach(function (el) {
        el.addEventListener('click', function () { toggle(+el.dataset.i, el); });
        el.addEventListener('mouseenter', function () { var i = +el.dataset.i; if (focusTask) showFocusFor(i); });
      });
    }

    var hoverIdx = -1;
    function showFocusFor(i) { var t = tasksFor(state.mode); if (focusTask && t[i]) { focusTask.textContent = 'Day ' + (i + 1) + ' · ' + t[i]; } }

    function toggle(i, el) {
      var k = key(state.mode, i), wasDone = !!state.done[k];
      if (wasDone) { delete state.done[k]; }
      else {
        state.done[k] = 1;
        if (el) sparkle(el);
        var dc = doneCount(), total = tasksFor(state.mode).length;
        if (dc === total) { toast('Challenge complete — beautiful work 🌿'); burst(true); }
        else if (dc % 7 === 0) { toast('Another week outside ☔'); burst(false); }
        else { toast('Logged — that’s a reset ☔'); }
      }
      LS.set('oszea_rc_30', state);
      render(false);
    }

    function setMode(mode) {
      state.mode = mode; LS.set('oszea_rc_30', state);
      modeBtns.forEach(function (b) { b.classList.toggle('active', b.dataset.mode === mode); });
      buildGrid(); render(true);
    }

    function render(animateGrid) {
      var m = modes[state.mode], tasks = tasksFor(state.mode), dc = doneCount();
      modeBtns.forEach(function (b) { b.classList.toggle('active', b.dataset.mode === state.mode); });
      if (titleEl) titleEl.textContent = m.label;
      if (subEl) subEl.textContent = 'One small outdoor reset · ' + tasks.length + ' ' + m.unit;

      $$('.rc-drop', grid).forEach(function (el) { el.classList.toggle('done', isDone(+el.dataset.i)); });

      var pct = tasks.length ? dc / tasks.length : 0;
      if (ringFg) ringFg.style.strokeDashoffset = CIRC * (1 - pct);
      if (ringTxt) ringTxt.textContent = Math.round(pct * 100) + '%';
      animateCount(doneEl, dc); animateCount(streakEl, streak()); animateCount(leftEl, tasks.length - dc);

      var fu = firstUndone();
      if (focusTask) {
        if (fu === -1) { focusTask.textContent = 'Every moment done — you’ve completed this challenge.'; }
        else { focusTask.textContent = 'Day ' + (fu + 1) + ' · ' + tasks[fu]; }
      }
      if (focusBtn) { focusBtn.dataset.i = fu; focusBtn.style.display = fu === -1 ? 'none' : ''; }

      // reflections every 7 completed
      var weeksDone = Math.floor(dc / 7);
      if (reflectBox) {
        if (weeksDone > 0 && REFLECTIONS[weeksDone - 1] && dc < tasks.length + 1) {
          reflectQ.textContent = REFLECTIONS[Math.min(weeksDone - 1, REFLECTIONS.length - 1)];
          reflectBox.hidden = false;
        } else { reflectBox.hidden = true; }
      }
      if (animateGrid) {
        $$('.rc-drop', grid).forEach(function (el, i) { el.style.animation = 'none'; void el.offsetWidth; el.style.animation = 'rc-drop-in .5s var(--ease-out) both'; el.style.animationDelay = (i * 0.018) + 's'; });
      }
    }

    function animateCount(el, to) {
      if (!el) return; var from = parseInt(el.textContent, 10) || 0; if (from === to) { el.textContent = to; return; }
      var steps = Math.min(Math.abs(to - from), 12), i = 0;
      var iv = setInterval(function () { i++; el.textContent = Math.round(from + (to - from) * (i / steps)); if (i >= steps) { el.textContent = to; clearInterval(iv); } }, 28);
    }

    /* ---- raindrop sparkle confetti ---- */
    var cctx = confetti ? confetti.getContext('2d') : null, parts = [], craf = null;
    function sizeC() { if (!confetti) return; var p = confetti.parentElement.getBoundingClientRect(); var dpr = Math.min(2, window.devicePixelRatio || 1); confetti.width = p.width * dpr; confetti.height = p.height * dpr; confetti.style.width = p.width + 'px'; confetti.style.height = p.height + 'px'; cctx.setTransform(dpr, 0, 0, dpr, 0, 0); }
    function sparkle(el) {
      if (!cctx || reduce) return; sizeC();
      var pr = confetti.parentElement.getBoundingClientRect(), er = el.getBoundingClientRect();
      var x = er.left - pr.left + er.width / 2, y = er.top - pr.top + er.height / 2;
      for (var i = 0; i < 12; i++) { var a = Math.random() * 6.28, sp = 1.5 + Math.random() * 3.5; parts.push({ x: x, y: y, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp - 1.5, life: 1, sz: 1.5 + Math.random() * 2.5, c: Math.random() > .5 ? '95,200,194' : '255,255,255' }); }
      runC();
    }
    function burst(big) {
      if (!cctx || reduce) return; sizeC();
      var pr = confetti.parentElement.getBoundingClientRect(), x = pr.width / 2, y = pr.height * 0.4, n = big ? 70 : 36;
      for (var i = 0; i < n; i++) { var a = Math.random() * 6.28, sp = 2 + Math.random() * (big ? 6 : 4); parts.push({ x: x, y: y, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp - 2, life: 1, sz: 1.5 + Math.random() * 3, c: Math.random() > .5 ? '95,200,194' : (Math.random() > .5 ? '232,99,42' : '255,255,255') }); }
      runC();
    }
    function runC() { if (!craf) craf = requestAnimationFrame(stepC); }
    function stepC() {
      cctx.clearRect(0, 0, confetti.width, confetti.height);
      parts = parts.filter(function (p) { return p.life > 0; });
      parts.forEach(function (p) { p.x += p.vx; p.y += p.vy; p.vy += 0.12; p.life -= 0.02; cctx.globalAlpha = Math.max(0, p.life); cctx.fillStyle = 'rgba(' + p.c + ',1)'; cctx.beginPath(); cctx.ellipse(p.x, p.y, p.sz * 0.7, p.sz * 1.3, 0, 0, 6.28); cctx.fill(); });
      cctx.globalAlpha = 1;
      if (parts.length) { craf = requestAnimationFrame(stepC); } else { craf = null; cctx.clearRect(0, 0, confetti.width, confetti.height); }
    }
    window.addEventListener('resize', sizeC, { passive: true });

    modeBtns.forEach(function (b) { b.addEventListener('click', function () { setMode(b.dataset.mode); }); });
    if (focusBtn) focusBtn.addEventListener('click', function () { var i = +focusBtn.dataset.i; if (i >= 0) { var el = $('.rc-drop[data-i="' + i + '"]', grid); toggle(i, el); } });
    var resetBtn = $('#rc30Reset');
    if (resetBtn) resetBtn.addEventListener('click', function () {
      Object.keys(state.done).forEach(function (k) { if (k.indexOf(state.mode + ':') === 0) delete state.done[k]; });
      LS.set('oszea_rc_30', state); render(true); toast('Challenge reset — fresh start');
    });

    buildGrid(); render(false);
  })();

  /* ============================================================
     BREATHING RESET
     ============================================================ */
  (function breathe() {
    var stage = $('#breathing'); if (!stage) return;
    var PHASES = [
      { key: 'in',   label: 'Breathe in', sub: 'Fill slowly, from the belly', secs: 4 },
      { key: 'hold', label: 'Hold',       sub: 'Stay soft — no strain',       secs: 2 },
      { key: 'out',  label: 'Breathe out',sub: 'Let everything go',           secs: 6 },
      { key: 'rest', label: 'Rest',       sub: 'Settle before the next',      secs: 2 }
    ];
    var CYCLE = PHASES.reduce(function (a, p) { return a + p.secs; }, 0); // 14s
    var orb = $('#rcOrb'), glow = $('#rcOrbGlow'), label = $('#rcOrbLabel'),
        stateEl = $('#rcBreatheState'), countEl = $('#rcBreatheCount'),
        timerEl = $('#rcBreatheTimer'), startBtn = $('#rcBreatheStart'),
        live = $('#rcBreatheLive'), arc = $('#rcArc'),
        pills = $$('#rcBreatheTimeline .bt-pill'),
        dotsWrap = $('#rcBreatheBreaths'), durWrap = $('#rcBreatheDur');

    var ARC_R = 122, ARC_C = 2 * Math.PI * ARC_R;
    if (arc) { arc.style.strokeDasharray = ARC_C; arc.style.strokeDashoffset = ARC_C; }

    var durMin = 1, cycles = 4, totalSecs = 56;
    function setDuration(min) {
      durMin = min;
      cycles = Math.max(2, Math.round(min * 60 / CYCLE));
      totalSecs = cycles * CYCLE;
      buildDots();
      reset();
    }
    function buildDots() {
      if (!dotsWrap) return;
      var h = '';
      for (var i = 0; i < cycles; i++) h += '<span class="bd" data-c="' + i + '"></span>';
      dotsWrap.innerHTML = h;
    }

    // rAF state
    var mode = 'idle'; // idle | running | paused | done
    var startTs = 0, accrued = 0, raf = null, lastPhase = -1;

    function fmt(s) { s = Math.max(0, Math.ceil(s)); var m = Math.floor(s / 60), r = s % 60; return m + ':' + (r < 10 ? '0' + r : r); }
    function easeInOut(t) { return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2; }

    // scale curve per phase progress (0..1) -> orb scale 0.6..1
    function phaseScale(key, p) {
      if (key === 'in')   return 0.6 + 0.4 * easeInOut(p);
      if (key === 'hold') return 1.0;
      if (key === 'out')  return 1.0 - 0.4 * easeInOut(p);
      return 0.6; // rest
    }
    function locate(elapsed) {
      var inCycle = elapsed % CYCLE, acc = 0, idx = 0, prog = 0;
      for (var i = 0; i < PHASES.length; i++) {
        if (inCycle < acc + PHASES[i].secs) { idx = i; prog = (inCycle - acc) / PHASES[i].secs; break; }
        acc += PHASES[i].secs;
      }
      return { idx: idx, prog: prog, cycle: Math.floor(elapsed / CYCLE) };
    }
    function setLabel(p) {
      if (label) { label.classList.add('swap'); }
      setTimeout(function () {
        if (stateEl) stateEl.textContent = p.label;
        if (countEl) countEl.textContent = p.sub;
        if (label) label.classList.remove('swap');
      }, 220);
      if (live) live.textContent = p.label;
    }
    function paintPills(idx, prog) {
      pills.forEach(function (el, i) {
        var fill = $('.bt-fill', el);
        el.classList.toggle('on', i === idx);
        if (!fill) return;
        if (i < idx) { fill.style.transition = 'none'; fill.style.transform = 'scaleX(1)'; }
        else if (i > idx) { fill.style.transition = 'none'; fill.style.transform = 'scaleX(0)'; }
        else { fill.style.transition = 'none'; fill.style.transform = 'scaleX(' + prog.toFixed(3) + ')'; }
      });
    }
    function paintDots(cycle, prog) {
      if (!dotsWrap) return;
      $$('.bd', dotsWrap).forEach(function (d, i) {
        d.classList.toggle('done', i < cycle);
        d.classList.toggle('cur', i === cycle);
      });
    }

    function frame(now) {
      if (mode !== 'running') return;
      var elapsed = accrued + (now - startTs) / 1000;
      if (elapsed >= totalSecs) { finish(); return; }
      var loc = locate(elapsed), p = PHASES[loc.idx];
      var sc = phaseScale(p.key, loc.prog);
      if (orb) orb.style.transform = 'scale(' + sc.toFixed(4) + ')';
      if (glow) { glow.style.transform = 'scale(' + (sc * 1.15).toFixed(4) + ')'; glow.style.opacity = (0.35 + 0.5 * ((sc - 0.6) / 0.4)).toFixed(3); }
      if (arc) arc.style.strokeDashoffset = ARC_C * (1 - elapsed / totalSecs);
      if (loc.idx !== lastPhase) { lastPhase = loc.idx; stage.setAttribute('data-phase', p.key); setLabel(p); }
      paintPills(loc.idx, loc.prog);
      paintDots(loc.cycle, loc.prog);
      window.__breathDir = (p.key === 'in') ? -1 : (p.key === 'out' ? 1 : 0);
      var secLeft = totalSecs - elapsed;
      if (timerEl) timerEl.textContent = fmt(secLeft);
      raf = requestAnimationFrame(frame);
    }

    function start() {
      if (mode === 'done') { accrued = 0; }
      mode = 'running'; stage.setAttribute('data-state', 'running');
      startTs = performance.now();
      lastPhase = -1;
      if (startBtn) { startBtn.querySelector('.lab').textContent = 'Pause'; startBtn.querySelector('.ply').style.display = 'none'; }
      if (reduce) { runReduced(); return; }
      raf = requestAnimationFrame(frame);
    }
    function pause() {
      if (mode !== 'running') return;
      accrued += (performance.now() - startTs) / 1000;
      mode = 'paused'; stage.setAttribute('data-state', 'paused');
      cancelAnimationFrame(raf); clearInterval(redTimer);
      window.__breathDir = 0;
      if (stateEl) stateEl.textContent = 'Paused';
      if (countEl) countEl.textContent = 'Tap to resume';
      if (startBtn) { startBtn.querySelector('.lab').textContent = 'Resume'; startBtn.querySelector('.ply').style.display = ''; }
    }
    function reset() {
      mode = 'idle'; cancelAnimationFrame(raf); clearInterval(redTimer);
      accrued = 0; lastPhase = -1; window.__breathDir = 0;
      stage.setAttribute('data-state', 'idle'); stage.setAttribute('data-phase', 'rest');
      if (orb) orb.style.transform = 'scale(0.62)';
      if (glow) { glow.style.transform = 'scale(0.7)'; glow.style.opacity = '0.4'; }
      if (arc) arc.style.strokeDashoffset = ARC_C;
      if (timerEl) timerEl.textContent = fmt(totalSecs);
      if (stateEl) stateEl.textContent = 'Ready';
      if (countEl) countEl.textContent = durMin + '-minute reset';
      if (startBtn) { startBtn.querySelector('.lab').textContent = 'Begin'; startBtn.querySelector('.ply').style.display = ''; }
      pills.forEach(function (el) { el.classList.remove('on'); var f = $('.bt-fill', el); if (f) { f.style.transition = 'none'; f.style.transform = 'scaleX(0)'; } });
      if (dotsWrap) $$('.bd', dotsWrap).forEach(function (d) { d.classList.remove('done', 'cur'); });
    }
    function finish() {
      mode = 'done'; cancelAnimationFrame(raf); clearInterval(redTimer);
      window.__breathDir = 0;
      stage.setAttribute('data-state', 'idle'); stage.setAttribute('data-phase', 'rest');
      if (orb) orb.style.transform = 'scale(0.78)';
      if (glow) { glow.style.transform = 'scale(0.9)'; glow.style.opacity = '0.6'; }
      if (arc) arc.style.strokeDashoffset = 0;
      if (timerEl) timerEl.textContent = '0:00';
      if (stateEl) stateEl.textContent = 'Well done';
      if (countEl) countEl.textContent = 'You found some calm';
      if (startBtn) { startBtn.querySelector('.lab').textContent = 'Go again'; startBtn.querySelector('.ply').style.display = ''; }
      pills.forEach(function (el) { el.classList.remove('on'); });
      if (dotsWrap) $$('.bd', dotsWrap).forEach(function (d) { d.classList.add('done'); d.classList.remove('cur'); });
      if (live) live.textContent = 'Breathing reset complete.';
      toast('That’s your calm minute 🌿');
    }

    // reduced-motion fallback: simple per-second timer, no rAF scaling
    var redTimer = null;
    function runReduced() {
      var elapsed = accrued;
      clearInterval(redTimer);
      redTimer = setInterval(function () {
        elapsed += 1; accrued = elapsed; startTs = performance.now();
        if (elapsed >= totalSecs) { finish(); return; }
        var loc = locate(elapsed), p = PHASES[loc.idx];
        if (loc.idx !== lastPhase) { lastPhase = loc.idx; stage.setAttribute('data-phase', p.key); setLabel(p); }
        paintPills(loc.idx, 1); paintDots(loc.cycle, 0);
        if (arc) arc.style.strokeDashoffset = ARC_C * (1 - elapsed / totalSecs);
        if (timerEl) timerEl.textContent = fmt(totalSecs - elapsed);
      }, 1000);
    }

    if (startBtn) startBtn.addEventListener('click', function () { (mode === 'running') ? pause() : start(); });
    var resetBtn = $('#rcBreatheReset');
    if (resetBtn) resetBtn.addEventListener('click', reset);
    if (durWrap) $$('button', durWrap).forEach(function (b) {
      b.addEventListener('click', function () {
        $$('button', durWrap).forEach(function (x) { x.classList.remove('active'); });
        b.classList.add('active'); setDuration(+b.dataset.min);
      });
    });

    setDuration(1);

    /* ---- orb particles (droplets drifting in/out with the breath) ---- */
    (function particles() {
      var c = $('#rcBreatheParticles'); if (!c || reduce) return;
      var ctx = c.getContext('2d'), wrap = $('#rcOrbWrap');
      var W = 0, H = 0, cx = 0, cy = 0, dpr = 1, pts = [], inView = false, running = false;
      function resize() {
        dpr = Math.min(2, window.devicePixelRatio || 1);
        var r = wrap.getBoundingClientRect(); W = r.width * 1.2; H = r.height * 1.2;
        c.width = Math.round(W * dpr); c.height = Math.round(H * dpr); ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        cx = W / 2; cy = H / 2;
      }
      function seed() {
        pts = [];
        for (var i = 0; i < 34; i++) {
          var ang = Math.random() * Math.PI * 2, rad = (0.22 + Math.random() * 0.34) * Math.min(W, H);
          pts.push({ ang: ang, rad: rad, base: rad, sz: 1 + Math.random() * 2.4, sp: 0.4 + Math.random() * 0.7, a: 0.2 + Math.random() * 0.5, tw: Math.random() * 6.28 });
        }
      }
      function loop() {
        ctx.clearRect(0, 0, W, H);
        var dir = window.__breathDir || 0;
        for (var i = 0; i < pts.length; i++) {
          var p = pts[i];
          p.rad += dir * p.sp * -0.9;            // inhale (-1) pulls inward, exhale (+1) pushes out
          var lo = p.base * 0.55, hi = p.base * 1.25;
          if (p.rad < lo) p.rad = lo; if (p.rad > hi) p.rad = hi;
          p.tw += 0.02;
          var x = cx + Math.cos(p.ang) * p.rad, y = cy + Math.sin(p.ang) * p.rad;
          var tw = 0.6 + 0.4 * Math.sin(p.tw);
          ctx.beginPath(); ctx.arc(x, y, p.sz, 0, 6.2832);
          ctx.fillStyle = 'rgba(150,225,218,' + (p.a * tw).toFixed(3) + ')'; ctx.fill();
        }
        if (inView) requestAnimationFrame(loop); else running = false;
      }
      function startP() { if (!running) { running = true; requestAnimationFrame(loop); } }
      resize(); seed();
      window.addEventListener('resize', function () { resize(); seed(); }, { passive: true });
      new IntersectionObserver(function (es) { es.forEach(function (e) { inView = e.isIntersecting; if (inView) startP(); }); }, { threshold: 0.05 }).observe(wrap);
    })();
  })();

  /* ============================================================
     CHALLENGE CARDS + FILTERS + MODAL
     ============================================================ */
  (function cards() {
    var grid = $('#rcCards'); if (!grid) return;
    var saved = LS.get('oszea_rc_saved', {});
    var done = LS.get('oszea_rc_done', {});
    var active = []; // active filters

    function cardHTML(c) {
      var tags = '<span class="ct diff">' + c.diff + '</span><span class="ct">' + c.type + '</span>';
      var wx = wxFor(c);
      return '<article class="rc-card reveal" data-id="' + c.id + '" data-tags="' + c.tags.join(' ') + '">' +
        '<div class="rc-card-inner">' +
        '<div class="rc-card-media">' +
          (c.img ? '<img src="' + window.__OSZEA_ASSET_BASE + encodeURI(c.img) + '" alt="' + c.title + '" class="ph" style="width: 100%; height: 100%; object-fit: cover; position: absolute; inset: 0;" loading="lazy">' : '<div class="ph"><span class="ph-label">[ Landscape Photo — ' + c.title + ' ]</span></div>') +
          '<span class="rc-card-num">No. ' + (c.n < 10 ? '0' + c.n : c.n) + '</span>' +
          '<span class="rc-card-flag">' + c.flag + '</span>' +
          '<span class="rc-card-wx">' + wx.icon + wx.label + '</span>' +
          '<span class="rc-card-stamp">Done</span>' +
          '<button class="rc-card-save' + (saved[c.id] ? ' saved' : '') + '" data-save="' + c.id + '" aria-label="Save this walk">' + I.heart + '</button>' +
        '</div>' +
        '<div class="rc-card-body">' +
          '<span class="notch l"></span><span class="notch r"></span>' +
          '<span class="rc-card-loc">' + I.pin + c.loc + ' · ' + (c.country === 'nz' ? 'NZ' : 'AU') + '</span>' +
          '<h3>' + c.title + '</h3>' +
          '<p class="rc-card-poem">“' + c.reflect + '”</p>' +
          '<div class="rc-card-tags">' + tags + '</div>' +
          '<div class="rc-card-foot">' +
            '<span class="rc-card-time">' + I.clock + c.time + '</span>' +
            '<span class="rc-card-start">Open challenge <span class="arw">→</span></span>' +
          '</div>' +
        '</div>' +
        '</div>' +
      '</article>';
    }

    function wxFor(c) {
      var t = c.tags;
      if (t.indexOf('rainy') >= 0) return { icon: I.rain, label: 'Best in rain' };
      if (t.indexOf('coastal') >= 0) return { icon: I.wave, label: 'Coastal' };
      if (t.indexOf('mountain') >= 0) return { icon: I.mountain, label: 'Mountain' };
      if (t.indexOf('forest') >= 0) return { icon: I.tree, label: 'Forest' };
      if (t.indexOf('city') >= 0) return { icon: I.building, label: 'City reset' };
      if (t.indexOf('water') >= 0) return { icon: I.droplet, label: 'Near water' };
      return { icon: I.foot, label: 'Walk' };
    }

    grid.innerHTML = CHALLENGES.map(cardHTML).join('');
    refreshDone();
    observeReveals();

    function refreshDone() {
      $$('.rc-card', grid).forEach(function (el) { el.classList.toggle('is-done', !!done[el.dataset.id]); });
    }

    // click → ripple + open modal (save handled separately)
    grid.addEventListener('click', function (e) {
      var saveBtn = e.target.closest('[data-save]');
      if (saveBtn) {
        e.stopPropagation();
        var id = saveBtn.getAttribute('data-save');
        if (saved[id]) { delete saved[id]; saveBtn.classList.remove('saved'); toast('Removed from saved'); }
        else { saved[id] = 1; saveBtn.classList.add('saved'); toast('Saved to My RainClub'); }
        LS.set('oszea_rc_saved', saved);
        window.dispatchEvent(new CustomEvent('rc-moments-changed'));
        return;
      }
      var card = e.target.closest('.rc-card'); if (!card) return;
      // water ripple at click point
      if (!reduce) {
        var r = card.getBoundingClientRect();
        var rip = document.createElement('span');
        rip.className = 'rc-ripple';
        var size = Math.max(r.width, r.height) * 1.3;
        rip.style.width = rip.style.height = size + 'px';
        rip.style.left = (e.clientX - r.left) + 'px';
        rip.style.top = (e.clientY - r.top) + 'px';
        card.querySelector('.rc-card-inner').appendChild(rip);
        setTimeout(function () { rip.remove(); }, 720);
      }
      openDetail(card.dataset.id);
    });

    // 3D tilt on hover (skip touch / reduced motion)
    if (!reduce && window.matchMedia('(hover: hover)').matches) {
      grid.addEventListener('pointermove', function (e) {
        var card = e.target.closest('.rc-card'); if (!card) return;
        var r = card.getBoundingClientRect();
        var px = (e.clientX - r.left) / r.width - 0.5, py = (e.clientY - r.top) / r.height - 0.5;
        card.style.transform = 'perspective(1000px) rotateY(' + (px * 7).toFixed(2) + 'deg) rotateX(' + (-py * 8).toFixed(2) + 'deg) translateY(-6px)';
      });
      grid.addEventListener('pointerleave', function () {
        $$('.rc-card', grid).forEach(function (c) { c.style.transform = ''; });
      }, true);
      grid.addEventListener('pointerout', function (e) {
        var card = e.target.closest('.rc-card');
        if (card && !card.contains(e.relatedTarget)) card.style.transform = '';
      });
    }

    /* ---- filters ---- */
    var filterBar = $('#rcFilters'), metaEl = $('#rcFilterMeta');
    function applyFilters() {
      var cards = $$('.rc-card', grid), shown = 0;
      cards.forEach(function (el) {
        var t = (el.dataset.tags || '').split(' ');
        var ok = active.every(function (f) { return t.indexOf(f) >= 0; });
        el.classList.toggle('hide', !ok);
        if (ok) shown++;
      });
      if (metaEl) metaEl.textContent = 'Showing ' + shown + ' of ' + CHALLENGES.length + ' challenges';
      var empty = $('#rcCardsEmpty');
      if (empty) empty.style.display = shown ? 'none' : 'block';
    }
    if (filterBar) {
      $$('.rc-filter', filterBar).forEach(function (b) {
        b.addEventListener('click', function () {
          var f = b.dataset.f;
          if (f === 'all') { active = []; $$('.rc-filter', filterBar).forEach(function (x) { x.classList.remove('on'); }); b.classList.add('on'); applyFilters(); return; }
          $('.rc-filter[data-f="all"]', filterBar).classList.remove('on');
          var idx = active.indexOf(f);
          if (idx >= 0) { active.splice(idx, 1); b.classList.remove('on'); } else { active.push(f); b.classList.add('on'); }
          if (!active.length) $('.rc-filter[data-f="all"]', filterBar).classList.add('on');
          applyFilters();
        });
      });
      applyFilters();
    }

    /* ---- detail modal ---- */
    var modal = $('#rcDetail');
    function openDetail(id) {
      var c = CHALLENGES.find(function (x) { return x.id === id; }); if (!c || !modal) return;
      $('#rdLoc1', modal).textContent = c.loc + ' · ' + (c.country === 'nz' ? 'New Zealand' : 'Australia');
      $('#rdLoc2', modal).textContent = c.title;
      $('#rdPh', modal).innerHTML = c.img
        ? '<img src="' + window.__OSZEA_ASSET_BASE + encodeURI(c.img) + '" alt="' + c.title + '" style="position:absolute;inset:0;width:100%;height:100%;object-fit:cover;" loading="lazy">'
        : '<span class="ph-label">[ Landscape Photo — ' + c.title + ' ]</span>';
      $('#rdTags', modal).innerHTML =
        '<span class="chip teal">' + c.diff + '</span>' +
        '<span class="chip">' + c.flag + ' ' + (c.country === 'nz' ? 'New Zealand' : 'Australia') + '</span>' +
        '<span class="chip">' + I.clock + ' ' + c.time + '</span>';
      $('#rdTask', modal).textContent = c.task;
      $('#rdNotice', modal).textContent = c.notice;
      $('#rdReflect', modal).textContent = '“' + c.reflect + '”';
      $('#rdFitName', modal).textContent = c.fit;
      var imgUrl = fitImage(c.fit);
      var fitPh = $('#rdFitPh', modal);
      if (imgUrl) {
        fitPh.innerHTML = '<img src="' + imgUrl + '" style="width:100%;height:100%;object-fit:contain;" alt="' + c.fit + '">';
      } else {
        fitPh.innerHTML = '<div class="ph"><span class="ph-label">' + c.fit.split(' ')[0] + '</span></div>';
      }
      var doneBtn = $('#rdDone', modal);
      doneBtn.dataset.id = c.id;
      var isDone = !!done[c.id];
      doneBtn.querySelector('.lab').textContent = isDone ? 'Completed ✓' : 'Mark as done';
      doneBtn.classList.toggle('btn-primary', !isDone);
      doneBtn.classList.toggle('btn-light', isDone);
      var fitBtn = $('#rdShop', modal); fitBtn.setAttribute('href', fitLink(c.fit));
      modal.dataset.id = c.id;
      modal.classList.add('open'); modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    }
    function closeDetail() {
      if (!modal) return;
      modal.classList.remove('open'); modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }
    function shareWalk(id) {
      var c = CHALLENGES.find(function (x) { return x.id === id; }); if (!c) return;
      var here = location.href.split('#')[0];
      var land = c.country === 'nz' ? 'NZ' : 'AU';
      var caption = 'Another one done with OSZEA RainClub ☔👟\n' +
        c.title + ' · ' + c.loc + ', ' + land + '\n“' + c.reflect + '”\n\n#RainClub @oszeaofficial #ForgetTheForecast';
      if (navigator.share) {
        navigator.share({ title: 'OSZEA RainClub — ' + c.title, text: caption, url: here }).catch(function () {});
        return;
      }
      var copyText = caption + '\n' + here;
      var openIG = function () { window.open('https://www.instagram.com/', '_blank', 'noopener'); };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(copyText).then(function () {
          toast('Caption copied — paste it into your Instagram story or post');
          openIG();
        }, function () { toast('Tag your walk #RainClub · @oszeaofficial'); openIG(); });
      } else { toast('Tag your walk #RainClub · @oszeaofficial'); openIG(); }
    }
    if (modal) {
      modal.addEventListener('click', function (e) {
        if (e.target.closest('[data-mclose]') || e.target.classList.contains('ohub-scrim')) closeDetail();
        if (e.target.closest('#rdShare')) { shareWalk(modal.dataset.id); return; }
        var d = e.target.closest('#rdDone');
        if (d) {
          var id = d.dataset.id;
          if (done[id]) { delete done[id]; toast('Marked as not done'); }
          else { done[id] = 1; toast('Logged — well walked 🌿'); }
          LS.set('oszea_rc_done', done);
          refreshDone();
          window.dispatchEvent(new CustomEvent('rc-moments-changed'));
          var isDone = !!done[id];
          d.querySelector('.lab').textContent = isDone ? 'Completed ✓' : 'Mark as done';
          d.classList.toggle('btn-primary', !isDone);
          d.classList.toggle('btn-light', isDone);
        }
      });
      document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && modal.classList.contains('open')) closeDetail(); });
    }
    window.__rcOpenDetail = openDetail;
  })();

  /* ============================================================
     MAP — Leaflet + OpenStreetMap public tiles: free, no account or API key.
     ============================================================ */
  (function map() {
    var mapEl = $('#rcMap'); if (!mapEl || typeof L === 'undefined') return;
    var rail = $('#rcMapRail'), countEl = $('#rcMapCount');
    var done = LS.get('oszea_rc_done', {});
    var country = 'all', land = 'all';

    var lmap = L.map(mapEl, {
      zoomControl: true, scrollWheelZoom: false, attributionControl: true,
      minZoom: 3, maxZoom: 12, worldCopyJump: true
    });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19
    }).addTo(lmap);

    var markers = {}, routeLine = null;
    function markerIcon(c, active) {
      var rainy = c.tags.indexOf('rainy') >= 0, isDone = !!done[c.id];
      var cls = 'rc-mark' + (rainy ? ' rain' : '') + (isDone ? ' done' : '') + (active ? ' active' : '');
      return L.divIcon({
        className: cls, iconSize: [22, 22], iconAnchor: [11, 11], popupAnchor: [0, -14],
        html: '<span class="rc-mark-ring"></span><span class="rc-mark-dot">' + (isDone ? '✓' : '') + '</span>'
      });
    }
    function popupHTML(c) {
      return '<div class="rc-pop">' +
        '<div class="rc-pop-loc">' + c.flag + ' ' + c.loc + '</div>' +
        '<div class="rc-pop-title">' + c.title + '</div>' +
        '<div class="rc-pop-meta">' + c.diff + ' · ' + c.time + ' · ' + c.cond + '</div>' +
        '<div class="rc-pop-q">“' + c.reflect + '”</div>' +
        '<button class="rc-pop-cta" data-open="' + c.id + '" type="button">Open challenge →</button>' +
        '</div>';
    }
    CHALLENGES.forEach(function (c) {
      if (typeof c.lat !== 'number') return;
      var m = L.marker([c.lat, c.lng], { icon: markerIcon(c, false), riseOnHover: true });
      m.bindPopup(popupHTML(c), { closeButton: false, className: 'rc-leaflet-pop', maxWidth: 240 });
      m.on('mouseover', function () { m.openPopup(); highlight(c.id); });
      m.on('click', function () { m.openPopup(); highlight(c.id); });
      m.addTo(lmap); markers[c.id] = m;
    });

    // open from popup CTA (delegated)
    mapEl.addEventListener('click', function (e) {
      var b = e.target.closest('[data-open]');
      if (b && window.__rcOpenDetail) window.__rcOpenDetail(b.getAttribute('data-open'));
    });

    // rail
    rail.innerHTML = CHALLENGES.map(function (c) {
      return '<div class="rail-card" style="display: block !important;" data-id="' + c.id + '" data-country="' + c.country + '" data-land="' + (c.land || '') + '" data-tags="' + c.tags.join(' ') + '">' +
        '<div style="grid-column: 1 / -1; display: block; width: 100%;">' +
          '<div class="rc-city" style="display: flex; align-items: center; gap: 8px;">' + I.pin + c.title + '</div>' +
          '<div class="rc-quote" style="margin-top: 4px; padding-left: 23px;">' + c.loc + ' · ' + c.cond + ' · ' + c.diff + '</div>' +
        '</div>' +
      '</div>';
    }).join('');

    function highlight(id) {
      $$('.rail-card', rail).forEach(function (r) {
        var on = r.dataset.id === id;
        r.classList.toggle('active', on);
        if (on) { var top = r.offsetTop - rail.offsetTop - 12; rail.scrollTo({ top: top, behavior: reduce ? 'auto' : 'smooth' }); }
      });
      // draw a soft route line from a hub to this point
      var c = CHALLENGES.find(function (x) { return x.id === id; });
      if (!c) return;
      var hub = c.country === 'nz' ? [-41.0, 174.0] : [-32.0, 145.0];
      if (routeLine) { lmap.removeLayer(routeLine); routeLine = null; }
      routeLine = L.polyline([hub, [c.lat, c.lng]], { className: 'rc-route', color: '#5FC8C2', weight: 2, opacity: 0.6, dashArray: '2 8' }).addTo(lmap);
    }

    $$('.rail-card', rail).forEach(function (r) {
      r.addEventListener('mouseenter', function () { var m = markers[r.dataset.id]; if (m) { m.openPopup(); highlight(r.dataset.id); } });
      r.addEventListener('click', function () { if (window.__rcOpenDetail) window.__rcOpenDetail(r.dataset.id); });
    });

    function inFilter(c) {
      var okC = country === 'all' || c.country === country;
      var okL = land === 'all' || c.land === land || c.tags.indexOf(land) >= 0;
      return okC && okL;
    }
    function applyFilters(fit) {
      var shown = [];
      CHALLENGES.forEach(function (c) {
        var m = markers[c.id]; if (!m) return;
        var ok = inFilter(c);
        if (ok) { if (!lmap.hasLayer(m)) m.addTo(lmap); shown.push(c); } else { if (lmap.hasLayer(m)) lmap.removeLayer(m); }
      });
      $$('.rail-card', rail).forEach(function (r) {
        var c = CHALLENGES.find(function (x) { return x.id === r.dataset.id; });
        r.style.display = (c && inFilter(c)) ? '' : 'none';
      });
      if (countEl) countEl.textContent = shown.length + (shown.length === 1 ? ' walk' : ' walks');
      if (routeLine) { lmap.removeLayer(routeLine); routeLine = null; }
      if (fit !== false && shown.length) {
        var b = L.latLngBounds(shown.map(function (c) { return [c.lat, c.lng]; }));
        lmap.fitBounds(b.pad(0.25), { animate: !reduce });
      }
    }

    $$('#rcMapFilter button').forEach(function (b) {
      b.addEventListener('click', function () {
        country = b.dataset.c; $$('#rcMapFilter button').forEach(function (x) { x.classList.toggle('active', x === b); });
        applyFilters();
      });
    });
    $$('#rcMapLand button').forEach(function (b) {
      b.addEventListener('click', function () {
        land = b.dataset.l; $$('#rcMapLand button').forEach(function (x) { x.classList.toggle('active', x === b); });
        applyFilters();
      });
    });

    // initial fit (only once the section is on screen so sizing is correct)
    var initialised = false;
    new IntersectionObserver(function (es, obs) {
      es.forEach(function (e) {
        if (e.isIntersecting && !initialised) {
          initialised = true;
          lmap.invalidateSize();
          applyFilters(true);
          obs.disconnect();
        }
      });
    }, { threshold: 0.05 }).observe(mapEl);
    // safety: set a starting view immediately so tiles load (centred on AU+NZ)
    lmap.setView([-37, 147], 3);
  })();

  /* ============================================================
     RITUALS — "Try this ritual" scrolls to breathing or toasts
     ============================================================ */
  $$('.rc-ritual .rt-cta').forEach(function (b) {
    b.addEventListener('click', function (e) {
      var r = b.closest('.rc-ritual'); var which = r ? r.getAttribute('data-r') : '';
      if (which === 'breath') {
        e.preventDefault();
        var t = $('#breathing'); if (t) { window.scrollTo({ top: t.getBoundingClientRect().top + window.scrollY - 70, behavior: reduce ? 'auto' : 'smooth' }); }
        return;
      }
      e.preventDefault();
      toast('Ritual saved — try it on your next walk 🌿');
    });
  });

  /* ============================================================
     SIGNUP — chips + email + success  (Klaviyo-ready)
     ============================================================ */
  (function signup() {
    var form = $('#rcSignupForm'); if (!form) return;
    var chips = $$('#rcChips .rc-chip');
    chips.forEach(function (c) { c.addEventListener('click', function () { c.classList.toggle('on'); }); });
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var input = $('input', form); var v = input.value.trim();
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v)) { input.style.outline = '2px solid var(--orange)'; input.focus(); return; }
      input.style.outline = '';
      // === INTEGRATION POINT: Klaviyo / Shopify Email ===
      // Subscribe `v` and pass selected chips as profile tags:
      var tags = chips.filter(function (c) { return c.classList.contains('on'); }).map(function (c) { return c.dataset.int; });
      var hiddenTags = $('#rcSignupFormTags');
      if (hiddenTags) hiddenTags.value = "newsletter, rainclub, " + tags.join(', ');
      fetch(form.action, { method: 'POST', body: new FormData(form) }).catch(function(){});
      var sec = $('#rcSignup');
      if (sec) sec.classList.add('joined');
      var sd = $('#rcSignupDetail');
      if (sd) {
        sd.textContent = tags.length
          ? 'We’ll tune your weekly prompts around ' + (tags.length > 2 ? tags.slice(0, 2).join(', ') + ' + more' : tags.join(' & ')) + '.'
          : 'Weekly nature prompts and challenge cards are on their way.';
      }
      toast('Welcome to the RainClub ☔');
    });
  })();

  /* ============================================================
     SAVE PROGRESS TO EMAIL  (no login — portable restore link)
     ============================================================ */
  (function progressSave() {
    var panel = $('#rcSave'); if (!panel) return;
    var form = $('#rcSaveForm'), email = $('#rcSaveEmail'),
        donePanel = $('#rcSaveDone'), emailOut = $('#rcSaveEmailOut'),
        linkInput = $('#rcSaveLink'), copyBtn = $('#rcSaveCopy'),
        emailBtn = $('#rcSaveEmailBtn'), againBtn = $('#rcSaveAgain'),
        restoreToggle = $('#rcSaveRestoreToggle'), restoreForm = $('#rcSaveRestoreForm'),
        restoreInput = $('#rcRestoreInput'), restoreCancel = $('#rcSaveRestoreCancel'),
        mainBlock = $('.rc-save-main', panel);
    var savedEmail = LS.get('oszea_rc_email', '');

    function showDone(addr) {
      LS.set('oszea_rc_email', addr);
      if (emailOut) emailOut.textContent = addr;
      if (linkInput) linkInput.value = restoreLink(addr);
      if (mainBlock) mainBlock.hidden = true;
      if (restoreForm) restoreForm.hidden = true;
      if (donePanel) donePanel.hidden = false;
    }
    function showForm() {
      if (donePanel) donePanel.hidden = true;
      if (restoreForm) restoreForm.hidden = true;
      if (mainBlock) mainBlock.hidden = false;
      if (email) { email.value = LS.get('oszea_rc_email', '') || ''; email.focus(); }
    }

    if (email && savedEmail) email.value = savedEmail;

    if (form) form.addEventListener('submit', function (e) {
      e.preventDefault();
      var v = (email.value || '').trim();
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v)) { email.style.outline = '2px solid var(--orange)'; email.focus(); return; }
      email.style.outline = '';
      var noteField = $('#rcSaveFormNote');
      if (noteField) noteField.value = "Restore Link: " + restoreLink(v);
      fetch(form.action, { method: 'POST', body: new FormData(form) }).catch(function(){});
      showDone(v);
      toast('Progress saved & linked to your email ☔');
    });

    if (copyBtn) copyBtn.addEventListener('click', function () {
      // refresh to latest progress at copy time
      if (linkInput) linkInput.value = restoreLink(LS.get('oszea_rc_email', ''));
      var ok = function () { toast('Restore link copied'); copyBtn.textContent = 'Copied ✓'; setTimeout(function () { copyBtn.textContent = 'Copy'; }, 1800); };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(linkInput.value).then(ok, function () { linkInput.select(); });
      } else { linkInput.select(); try { document.execCommand('copy'); ok(); } catch (e) {} }
    });

    if (emailBtn) emailBtn.addEventListener('click', function () {
      var addr = LS.get('oszea_rc_email', '');
      // === INTEGRATION POINT: send the restore link to `addr` via Klaviyo/Shopify ===
      //   klaviyo.track('Email RainClub restore link', { $email: addr, restore_url: restoreLink(addr) });
      toast('On its way to ' + addr + ' — check your inbox');
      emailBtn.textContent = 'Sent ✓';
      setTimeout(function () { emailBtn.textContent = 'Email me this link'; }, 2200);
    });

    if (againBtn) againBtn.addEventListener('click', showForm);

    if (restoreToggle) restoreToggle.addEventListener('click', function () {
      if (mainBlock) mainBlock.hidden = true;
      if (restoreForm) { restoreForm.hidden = false; if (restoreInput) restoreInput.focus(); }
    });
    if (restoreCancel) restoreCancel.addEventListener('click', showForm);
    if (restoreForm) restoreForm.addEventListener('submit', function (e) {
      e.preventDefault();
      var raw = (restoreInput.value || '').trim();
      var m = raw.match(/restore=([^&\s]+)/);
      var code = m ? decodeURIComponent(m[1]) : raw;
      var obj = decodeProgress(code);
      if (!obj || !applyProgress(obj)) { restoreInput.style.outline = '2px solid var(--orange)'; return; }
      restoreInput.style.outline = '';
      toast('Progress restored — reloading ☔');
      setTimeout(function () { location.hash = '#moments'; location.reload(); }, 700);
    });

    if (window.__rcRestored) {
      toast(typeof window.__rcRestored === 'string' ? 'Welcome back — progress restored ✓' : 'Progress restored ✓');
      if (savedEmail || typeof window.__rcRestored === 'string') showDone(LS.get('oszea_rc_email', '') || window.__rcRestored);
    }

    $$('[data-rc-savejump]').forEach(function (b) {
      b.addEventListener('click', function (e) {
        e.preventDefault();
        panel.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
        setTimeout(function () { if (donePanel && donePanel.hidden && email) email.focus(); }, reduce ? 0 : 480);
      });
    });
  })();

  /* ============================================================
     FAQ ACCORDION
     ============================================================ */
  $$('.rc-faq-item').forEach(function (item) {
    var q = $('.rc-faq-q', item), a = $('.rc-faq-a', item);
    if (!q || !a) return;
    q.addEventListener('click', function () {
      var open = item.classList.contains('open');
      $$('.rc-faq-item.open').forEach(function (o) { if (o !== item) { o.classList.remove('open'); var oa = $('.rc-faq-a', o); if (oa) oa.style.maxHeight = '0px'; } });
      if (open) { item.classList.remove('open'); a.style.maxHeight = '0px'; }
      else { item.classList.add('open'); a.style.maxHeight = a.scrollHeight + 'px'; }
    });
  });
  window.addEventListener('resize', function () {
    var o = $('.rc-faq-item.open'); if (o) { var a = $('.rc-faq-a', o); if (a) a.style.maxHeight = a.scrollHeight + 'px'; }
  }, { passive: true });

  /* ============================================================
     MY RAINCLUB MOMENTS (saved + completed, from localStorage)
     ============================================================ */
  (function moments() {
    var wrap = $('#rcMoments'); if (!wrap) return;
    var grid = $('#rcMoGrid'), emptyTxt = $('#rcMoEmptyTxt'),
        doneN = $('#rcMoDoneN'), savedN = $('#rcMoSavedN'),
        summary = $('#rcMoSummary'), tabs = $$('#rcMoTabs button');
    var tab = 'done';

    function byId(id) { return CHALLENGES.find(function (c) { return c.id === id; }); }
    function ids(obj) { return Object.keys(obj || {}).filter(function (k) { return obj[k]; }); }

    function render() {
      var done = LS.get('oszea_rc_done', {}), saved = LS.get('oszea_rc_saved', {});
      var dIds = ids(done), sIds = ids(saved);
      if (doneN) doneN.textContent = dIds.length;
      if (savedN) savedN.textContent = sIds.length;
      var list = (tab === 'done' ? dIds : sIds).map(byId).filter(Boolean);
      if (summary) summary.textContent = dIds.length + ' completed · ' + sIds.length + ' saved';

      if (!list.length) {
        wrap.classList.add('empty');
        if (emptyTxt) emptyTxt.textContent = tab === 'done'
          ? 'You haven’t completed a walk yet. Open a challenge and mark it done to see it here.'
          : 'No saved walks yet. Tap the heart on any walk to save it for later.';
        grid.innerHTML = '';
        return;
      }
      wrap.classList.remove('empty');
      grid.innerHTML = list.map(function (c, i) {
        var badge = tab === 'done'
          ? '<span class="rc-mo-badge done"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.2" stroke-linecap="round" stroke-linejoin="round"><path d="m5 12 5 5L20 6"/></svg></span>'
          : '<span class="rc-mo-badge saved">' + I.heart + '</span>';
        var thumb = c.img
          ? '<img src="' + window.__OSZEA_ASSET_BASE + encodeURI(c.img) + '" alt="' + c.title + '" style="position:absolute;inset:0;width:100%;height:100%;object-fit:cover;" loading="lazy">'
          : '<div class="ph"><span class="ph-label">' + c.loc + '</span></div>';
        return '<div class="rc-mo-card" data-id="' + c.id + '" style="animation-delay:' + (i * 0.03) + 's">' +
          '<div class="rc-mo-thumb">' + badge + thumb + '</div>' +
          '<div class="rc-mo-meta"><div class="mm-loc">' + c.flag + ' ' + c.loc + '</div>' +
          '<div class="mm-t">' + c.title + '</div>' +
          '<div class="mm-s">' + c.diff + ' · ' + c.time + '</div></div>' +
          '<button class="rc-mo-x" data-remove="' + c.id + '" aria-label="Remove">' +
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><path d="M6 6l12 12M18 6 6 18"/></svg></button>' +
          '</div>';
      }).join('');
    }

    grid.addEventListener('click', function (e) {
      var rm = e.target.closest('[data-remove]');
      if (rm) {
        e.stopPropagation();
        var id = rm.getAttribute('data-remove');
        if (tab === 'done') { var d = LS.get('oszea_rc_done', {}); delete d[id]; LS.set('oszea_rc_done', d); toast('Removed from completed'); }
        else { var s = LS.get('oszea_rc_saved', {}); delete s[id]; LS.set('oszea_rc_saved', s); toast('Removed from saved'); }
        window.dispatchEvent(new CustomEvent('rc-moments-changed'));
        return;
      }
      var card = e.target.closest('.rc-mo-card');
      if (card && window.__rcOpenDetail) window.__rcOpenDetail(card.dataset.id);
    });

    tabs.forEach(function (b) { b.addEventListener('click', function () { tab = b.dataset.t; tabs.forEach(function (x) { x.classList.toggle('active', x === b); }); render(); }); });
    window.addEventListener('rc-moments-changed', render);
    render();
  })();

  /* ============================================================
     SHARED CHROME (nav, mobile menu, footer, rain, water)
     ============================================================ */
  var navZone = $('#navZone');
  if (navZone) {
    var onScroll = function () { navZone.classList.toggle('scrolled', window.scrollY > 20); };
    window.addEventListener('scroll', onScroll, { passive: true }); onScroll();
  }
  var ham = $('#hamBtn'), mmenu = $('#mmenu');
  if (ham && mmenu) {
    ham.addEventListener('click', function () { mmenu.classList.add('open'); document.body.style.overflow = 'hidden'; });
    mmenu.addEventListener('click', function (e) { if (e.target.closest('[data-mclose]') || e.target.classList.contains('scrim')) { mmenu.classList.remove('open'); document.body.style.overflow = ''; } });
  }
  $$('.foot-col').forEach(function (col) {
    var h = $('h4', col);
    if (h) h.addEventListener('click', function () { if (window.matchMedia('(max-width:560px)').matches) col.classList.toggle('open'); });
  });
  var track = $('#tickerTrack');
  if (track) {
    var items = ['Auckland drizzle', 'Wellington wind', 'Melbourne mood swings', 'Sydney sea air', 'Queenstown cold snaps', 'Brisbane humidity', 'Christchurch chill', 'Blue Mountains haze'];
    var tk = items.map(function (t) { return '<span class="foot-ticker-item">' + t + '<span class="tdot"></span></span>'; }).join('');
    track.innerHTML = tk + tk;
  }
  // currency toggle (announce bar)
  var curBtn = $('#curBtn');
  if (curBtn) {
    var cur = 'NZD';
    curBtn.addEventListener('click', function () {
      cur = cur === 'NZD' ? 'AUD' : 'NZD';
      var flag = cur === 'NZD' ? '🇳🇿' : '🇦🇺', other = cur === 'NZD' ? '/ AUD' : '/ NZD';
      curBtn.innerHTML = '<span class="flag">' + flag + '</span><span>' + cur + '</span><span class="label-hide" style="opacity:.5">' + other + '</span>';
      toast('Prices now showing in ' + cur);
    });
  }

  /* ---------- Rain canvas (hero / breathing / final) ---------- */
  function initRain(canvas, opts) {
    if (!canvas || reduce) return;
    opts = opts || {};
    var ctx = canvas.getContext('2d'), host = canvas.parentElement;
    var W = 0, H = 0, dpr = 1, drops = [], running = false, inView = false;
    var COUNT = opts.count || 80, COLOR = opts.color || '160,205,215';
    function resize() { dpr = Math.min(2, window.devicePixelRatio || 1); var r = host.getBoundingClientRect(); W = r.width; H = r.height; canvas.width = Math.round(W * dpr); canvas.height = Math.round(H * dpr); ctx.setTransform(dpr, 0, 0, dpr, 0, 0); }
    function seed() { drops = []; for (var i = 0; i < COUNT; i++) drops.push({ x: Math.random() * W, y: Math.random() * H, len: 9 + Math.random() * 16, v: 6 + Math.random() * 8, a: 0.05 + Math.random() * 0.14 }); }
    function loop() {
      ctx.clearRect(0, 0, W, H); ctx.lineWidth = 1.1;
      for (var i = 0; i < drops.length; i++) { var d = drops[i]; ctx.strokeStyle = 'rgba(' + COLOR + ',' + d.a + ')'; ctx.beginPath(); ctx.moveTo(d.x, d.y); ctx.lineTo(d.x - 1.5, d.y + d.len); ctx.stroke(); d.y += d.v; d.x -= 0.4; if (d.y > H) { d.y = -d.len; d.x = Math.random() * W; } }
      if (inView) requestAnimationFrame(loop); else running = false;
    }
    function start() { if (!running) { running = true; requestAnimationFrame(loop); } }
    resize(); seed();
    window.addEventListener('resize', function () { resize(); seed(); }, { passive: true });
    new IntersectionObserver(function (es) { es.forEach(function (e) { inView = e.isIntersecting; if (inView) start(); }); }, { threshold: 0.02 }).observe(host);
  }
  initRain($('#rcHeroRain'), { count: 80, color: '170,210,220' });
  initRain($('#rcBreatheRain'), { count: 46, color: '120,200,196' });
  initRain($('#rcFinalRain'), { count: 70, color: '170,210,220' });

  /* ---- hero parallax (mist/words shift with the mouse) + CTA ripple ---- */
  (function heroFx() {
    var hero = $('#rcTop'); if (!hero || reduce) return;
    var media = $('#rcHeroMedia'), words = $('#rcHeroWords');
    hero.addEventListener('pointermove', function (e) {
      var r = hero.getBoundingClientRect();
      var px = (e.clientX - r.left) / r.width - 0.5, py = (e.clientY - r.top) / r.height - 0.5;
      if (media) media.style.transform = 'scale(1.06) translate(' + (px * -16).toFixed(1) + 'px,' + (py * -12).toFixed(1) + 'px)';
      if (words) words.style.transform = 'translate(' + (px * 22).toFixed(1) + 'px,' + (py * 16).toFixed(1) + 'px)';
    });
    hero.addEventListener('pointerleave', function () {
      if (media) media.style.transform = ''; if (words) words.style.transform = '';
    });
  })();
  $$('.rc-ripple-btn').forEach(function (b) {
    b.addEventListener('pointermove', function (e) {
      var r = b.getBoundingClientRect();
      b.style.setProperty('--rx', (e.clientX - r.left) + 'px');
      b.style.setProperty('--ry', (e.clientY - r.top) + 'px');
    });
  });

  /* ---- Water Keeps Moving: reflection prompts ---- */
  (function waterPrompts() {
    var wrap = $('#rcWaterPrompts'), bubble = $('#rcWaterBubble'), hint = $('#rcWaterHint');
    if (!wrap || !bubble) return;
    var nodes = $$('.rc-wp', wrap);
    function show(node) {
      nodes.forEach(function (n) { n.classList.toggle('on', n === node); });
      bubble.textContent = '“' + node.dataset.q + '”';
      bubble.style.left = node.style.getPropertyValue('--p');
      bubble.classList.add('show');
      if (hint) hint.classList.add('hide');
    }
    function hide() { nodes.forEach(function (n) { n.classList.remove('on'); }); bubble.classList.remove('show'); }
    nodes.forEach(function (n) {
      n.addEventListener('mouseenter', function () { show(n); });
      n.addEventListener('focus', function () { show(n); });
      n.addEventListener('click', function () { show(n); });
    });
    wrap.addEventListener('mouseleave', hide);
  })();

  /* ---------- Interactive water (Water Keeps Moving) ----------
     Waves swell toward the pointer; dragging beads up glowing
     droplets that cling, then roll off downward and fade — like
     rain beading and rolling off a ZEA-TEX boot. Click = ripple. */
  (function waterLines() {
    var c = $('#rcWaterCanvas'); if (!c) return;
    var ctx = c.getContext('2d'), host = c.parentElement, hint = $('#rcWaterHint');
    var W = 0, H = 0, dpr = 1, t = 0, inView = false, running = false;
    var px = -999, py = -999, hot = false, lastBead = 0, beads = [], rings = [];
    var TEAL = '95,200,194', LIGHT = '150,225,218';

    function resize() {
      dpr = Math.min(2, window.devicePixelRatio || 1);
      var r = host.getBoundingClientRect(); W = r.width; H = r.height;
      c.width = Math.round(W * dpr); c.height = Math.round(H * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }
    resize(); window.addEventListener('resize', resize, { passive: true });

    function spawnBead(x, y) {
      beads.push({ x: x + (Math.random() - 0.5) * 12, y: y, vy: 0, r: 1.5,
        grow: 4 + Math.random() * 7, a: 0.95, held: 0.18 + Math.random() * 0.3,
        stuck: true, wob: Math.random() * 6.28 });
      if (beads.length > 100) beads.shift();
    }
    function spawnRing(x, y) { rings.push({ x: x, y: y, r: 6, max: 56 + Math.random() * 44, a: 0.55 }); if (rings.length > 24) rings.shift(); }

    if (!reduce) {
      host.addEventListener('pointermove', function (e) {
        var r = host.getBoundingClientRect();
        px = e.clientX - r.left; py = e.clientY - r.top; hot = true;
        var now = performance.now();
        if (now - lastBead > 42) { spawnBead(px, py); lastBead = now; }
        if (hint) hint.classList.add('hide');
        start();
      }, { passive: true });
      host.addEventListener('pointerleave', function () { hot = false; });
      host.addEventListener('pointerdown', function (e) {
        var r = host.getBoundingClientRect();
        var x = e.clientX - r.left, y = e.clientY - r.top;
        spawnRing(x, y);
        for (var i = 0; i < 7; i++) spawnBead(x + (Math.random() - 0.5) * 44, y + (Math.random() - 0.5) * 22);
        if (hint) hint.classList.add('hide');
        start();
      });
    }

    function drawBead(b) {
      var grd = ctx.createRadialGradient(b.x - b.r * 0.3, b.y - b.r * 0.4, 0, b.x, b.y, b.r * 1.6);
      grd.addColorStop(0, 'rgba(' + LIGHT + ',' + (0.9 * b.a).toFixed(3) + ')');
      grd.addColorStop(0.5, 'rgba(' + TEAL + ',' + (0.5 * b.a).toFixed(3) + ')');
      grd.addColorStop(1, 'rgba(' + TEAL + ',0)');
      ctx.beginPath(); ctx.arc(b.x, b.y, b.r * 1.5, 0, 6.2832); ctx.fillStyle = grd; ctx.fill();
      ctx.beginPath(); ctx.arc(b.x - b.r * 0.25, b.y - b.r * 0.3, Math.max(0.6, b.r * 0.32), 0, 6.2832);
      ctx.fillStyle = 'rgba(255,255,255,' + (0.85 * b.a).toFixed(3) + ')'; ctx.fill();
    }

    function loop() {
      t += 0.006; ctx.clearRect(0, 0, W, H);

      for (var line = 0; line < 5; line++) {
        var baseY = H * (0.3 + line * 0.12);
        ctx.beginPath();
        for (var x = 0; x <= W; x += 8) {
          var y = baseY + Math.sin(x * 0.012 + t * (1 + line * 0.3) + line) * (10 + line * 3) + Math.sin(x * 0.03 - t * 2) * 4;
          if (hot) {
            var dx = x - px, infl = Math.exp(-(dx * dx) / (2 * 95 * 95));
            y += (py - baseY) * 0.16 * infl - 12 * infl * Math.sin(t * 6);
          }
          if (x === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
        }
        ctx.strokeStyle = 'rgba(' + TEAL + ',' + (0.16 - line * 0.02) + ')';
        ctx.lineWidth = 1.4; ctx.stroke();
      }

      if (hot) {
        var gr = ctx.createRadialGradient(px, py, 0, px, py, 130);
        gr.addColorStop(0, 'rgba(' + TEAL + ',0.13)'); gr.addColorStop(1, 'rgba(' + TEAL + ',0)');
        ctx.fillStyle = gr; ctx.fillRect(px - 130, py - 130, 260, 260);
      }

      rings = rings.filter(function (rp) { return rp.a > 0.02; });
      rings.forEach(function (rp) {
        rp.r += (rp.max - rp.r) * 0.07; rp.a *= 0.94;
        ctx.beginPath(); ctx.arc(rp.x, rp.y, rp.r, 0, 6.2832);
        ctx.strokeStyle = 'rgba(' + LIGHT + ',' + rp.a.toFixed(3) + ')'; ctx.lineWidth = 1.4; ctx.stroke();
      });

      for (var i = beads.length - 1; i >= 0; i--) {
        var b = beads[i];
        if (b.r < b.grow) b.r += (b.grow - b.r) * 0.12;
        if (b.stuck) { b.held -= 0.016; if (b.held <= 0) b.stuck = false; }
        else { b.vy += 0.22; b.y += b.vy; b.wob += 0.3; b.x += Math.sin(b.wob) * 0.5; b.a *= 0.992; }
        drawBead(b);
        if (b.y - b.r > H || b.a < 0.05) beads.splice(i, 1);
      }

      var busy = inView || hot || beads.length || rings.length;
      if (busy && !reduce) requestAnimationFrame(loop); else running = false;
    }
    function start() { if (!running) { running = true; requestAnimationFrame(loop); } }
    new IntersectionObserver(function (es) { es.forEach(function (e) { inView = e.isIntersecting; if (inView) { resize(); start(); } }); }, { threshold: 0.02 }).observe(host);
  })();

  /* ---------- Footer water canvas ---------- */
  (function initFootWater() {
    var c = document.getElementById('footWater'); if (!c || reduce) return;
    var footer = c.closest('.footer'); var ctx = c.getContext('2d');
    var W = 0, H = 0, dpr = 1, ripples = [], mx = -999, my = -999, hot = false, inView = false, running = false, t = 0, lastSpawn = 0;
    var TEAL = '95,200,194';
    function resize() { dpr = Math.min(2, window.devicePixelRatio || 1); var r = footer.getBoundingClientRect(); W = r.width; H = r.height; c.width = Math.round(W * dpr); c.height = Math.round(H * dpr); ctx.setTransform(dpr, 0, 0, dpr, 0, 0); }
    resize(); window.addEventListener('resize', resize, { passive: true });
    function spawn(x, y, s) { ripples.push({ x: x, y: y, r: 5, max: 70 * s, a: 0.55 }); if (ripples.length > 36) ripples.shift(); }
    footer.addEventListener('pointermove', function (e) { var r = footer.getBoundingClientRect(); mx = e.clientX - r.left; my = e.clientY - r.top; hot = true; var now = performance.now(); if (now - lastSpawn > 90) { spawn(mx, my, 1); lastSpawn = now; } start(); });
    footer.addEventListener('pointerleave', function () { hot = false; });
    footer.addEventListener('pointerdown', function (e) { var r = footer.getBoundingClientRect(); spawn(e.clientX - r.left, e.clientY - r.top, 2.6); start(); });
    function start() { if (!running) { running = true; requestAnimationFrame(loop); } }
    function loop() {
      t += 0.016; ctx.clearRect(0, 0, W, H); ctx.globalCompositeOperation = 'lighter';
      for (var i = 0; i < 4; i++) { var x = W * (0.16 + 0.24 * i) + Math.sin(t * 0.3 + i * 1.3) * 80; var y = H * 0.52 + Math.cos(t * 0.38 + i * 1.9) * 52; var rad = 150 + Math.sin(t * 0.5 + i) * 30; var g = ctx.createRadialGradient(x, y, 0, x, y, rad); g.addColorStop(0, 'rgba(' + TEAL + ',0.07)'); g.addColorStop(1, 'rgba(' + TEAL + ',0)'); ctx.fillStyle = g; ctx.fillRect(0, 0, W, H); }
      if (hot) { var crad = 150 + Math.sin(t * 3) * 14; var cg = ctx.createRadialGradient(mx, my, 0, mx, my, crad); cg.addColorStop(0, 'rgba(' + TEAL + ',0.16)'); cg.addColorStop(0.5, 'rgba(' + TEAL + ',0.05)'); cg.addColorStop(1, 'rgba(' + TEAL + ',0)'); ctx.fillStyle = cg; ctx.fillRect(0, 0, W, H); }
      ctx.globalCompositeOperation = 'source-over';
      ripples = ripples.filter(function (rp) { return rp.a > 0.012; });
      ripples.forEach(function (rp) { rp.r += (rp.max - rp.r) * 0.06; rp.a *= 0.955; ctx.beginPath(); ctx.arc(rp.x, rp.y, rp.r, 0, 6.2832); ctx.strokeStyle = 'rgba(' + TEAL + ',' + rp.a + ')'; ctx.lineWidth = 1.6; ctx.stroke(); ctx.beginPath(); ctx.arc(rp.x, rp.y, rp.r * 0.62, 0, 6.2832); ctx.strokeStyle = 'rgba(' + TEAL + ',' + (rp.a * 0.5) + ')'; ctx.lineWidth = 1.1; ctx.stroke(); });
      if (inView || ripples.length) { requestAnimationFrame(loop); } else { running = false; }
    }
    new IntersectionObserver(function (es) { es.forEach(function (e) { inView = e.isIntersecting; if (inView) { resize(); start(); } }); }, { threshold: 0.02 }).observe(footer);
  })();
})();
